import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@runcheetah/ui";
import {
  CodeText,
  formatMoneyUsd,
  PageHeader,
  QueryErrorState,
  SegmentedControl,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChartFrame, ChartTooltipContent } from "@/components/ui/chart";
import { formatNullableDateTimeWithSeconds } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type ProductStatsOverviewInput = RouterInputs["admin"]["stats"]["overview"];
type ProductStatsOverview = RouterOutputs["admin"]["stats"]["overview"];
type DurationSummary = ProductStatsOverview["kpis"]["onboarding"];
type BrandErrorRate = ProductStatsOverview["brandErrorRates"][number];
type StatusCount = ProductStatsOverview["statusCounts"]["brands"][number];

type ProductStatsRange = "7d" | "30d" | "90d";

const RANGE_OPTIONS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
] as const;

const TREND_COLORS = {
  newBrands: "#171717",
  completedOnboardings: "#2563eb",
  generatedPosts: "#059669",
  failedWorkerRuns: "#dc2626",
} as const;

const DURATION_COLOR = "#171717";
const MS_PER_DAY = 24 * 60 * 60 * 1_000;

const getUtcDayStart = (value: Date): Date =>
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));

const getUtcDayEnd = (value: Date): Date =>
  new Date(getUtcDayStart(value).getTime() + MS_PER_DAY - 1);

export const buildProductStatsOverviewInput = (
  range: ProductStatsRange,
  now = new Date(),
): ProductStatsOverviewInput => {
  const days = range === "7d" ? 7 : range === "90d" ? 90 : 30;
  const to = getUtcDayEnd(now);
  const from = new Date(getUtcDayStart(now).getTime() - (days - 1) * MS_PER_DAY);

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
};

export const formatProductStatsDuration = (valueMs: number | null): string => {
  if (valueMs === null) {
    return "-";
  }

  const minutes = valueMs / 60_000;
  if (minutes < 1) {
    return "<1m";
  }

  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }

  const hours = minutes / 60;
  if (hours < 24) {
    return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
  }

  const days = hours / 24;
  return `${days.toFixed(days >= 10 ? 0 : 1)}d`;
};

export const formatProductStatsRate = (value: number): string =>
  `${(value * 100).toFixed(value >= 0.1 ? 1 : 2)}%`;

export const formatProductStatsInteger = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    notation: value >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: 0,
  }).format(value);

export const formatProductStatsStatusLabel = (status: string): string =>
  status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatTrendDay = (value: string): string => {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(parsed);
};

const formatDateTime = (value: string | null): string =>
  value ? formatNullableDateTimeWithSeconds(value) : "-";

function formatJobTypeLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function getStatusToneClass(status: string): string {
  if (status.includes("failed") || status === "error" || status === "revoked") {
    return "border-red-200 bg-red-50 text-red-700";
  }

  if (status.includes("processing") || status.includes("queued") || status === "publishing") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (status.includes("done") || status.includes("published") || status === "connected") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  return "border-border bg-muted text-muted-foreground";
}

function sumTrend(
  data: ProductStatsOverview["trend"],
  key: keyof ProductStatsOverview["trend"][number],
) {
  return data.reduce((sum, item) => {
    const value = item[key];
    return typeof value === "number" ? sum + value : sum;
  }, 0);
}

function MetricCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "good" | "warning" | "danger";
}) {
  return (
    <Card
      className={cn(
        "overflow-hidden border bg-card shadow-xs",
        tone === "danger" && "border-red-200 bg-red-50/50",
        tone === "warning" && "border-amber-200 bg-amber-50/50",
        tone === "good" && "border-emerald-200 bg-emerald-50/40",
      )}
    >
      <CardContent className="p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="mt-3 text-3xl font-semibold tracking-tight text-foreground">{value}</div>
        <div className="mt-2 text-xs leading-5 text-muted-foreground">{detail}</div>
      </CardContent>
    </Card>
  );
}

function formatDurationDetail(summary: DurationSummary): string {
  return `Avg ${formatProductStatsDuration(summary.averageMs)} / p90 ${formatProductStatsDuration(
    summary.p90Ms,
  )} / ${summary.sampleCount} samples`;
}

export function ProductStatsKpiGrid({ overview }: { overview: ProductStatsOverview }) {
  const generatedPostCount = sumTrend(overview.trend, "generatedPosts");
  const workerErrorRate = overview.kpis.workerErrors.errorRate;
  const publishFailureRate = overview.kpis.publishing.failureRate;

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
      <MetricCard
        label="Onboarding Time"
        value={formatProductStatsDuration(overview.kpis.onboarding.medianMs)}
        detail={formatDurationDetail(overview.kpis.onboarding)}
      />
      <MetricCard
        label="Post Generation"
        value={formatProductStatsDuration(overview.kpis.socialPostGeneration.medianMs)}
        detail={formatDurationDetail(overview.kpis.socialPostGeneration)}
      />
      <MetricCard
        label="Worker Error Rate"
        value={formatProductStatsRate(workerErrorRate)}
        detail={`${overview.kpis.workerErrors.errorRuns} errors / ${overview.kpis.workerErrors.totalRuns} runs`}
        tone={workerErrorRate >= 0.1 ? "danger" : workerErrorRate > 0 ? "warning" : "good"}
      />
      <MetricCard
        label="LLM Cost"
        value={formatMoneyUsd(overview.kpis.llmCost.totalCostUsd, { precision: 2 })}
        detail={`${overview.kpis.llmCost.eventCount} usage events`}
      />
      <MetricCard
        label="Generated Posts"
        value={formatProductStatsInteger(generatedPostCount)}
        detail={`Across ${overview.window.days} days`}
      />
      <MetricCard
        label="Publish Failures"
        value={formatProductStatsRate(publishFailureRate)}
        detail={`${overview.kpis.publishing.failedCount} failed / ${overview.kpis.publishing.totalPublications} attempts`}
        tone={publishFailureRate >= 0.1 ? "danger" : publishFailureRate > 0 ? "warning" : "good"}
      />
    </div>
  );
}

function TrendChart({ overview }: { overview: ProductStatsOverview }) {
  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <CardTitle className="text-sm">Daily Product Flow</CardTitle>
        <p className="text-sm text-muted-foreground">
          Brand creation, completed onboardings, generated posts, and failed worker runs.
        </p>
      </CardHeader>
      <CardContent>
        <ChartFrame className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={overview.trend} margin={{ left: 4, right: 12, top: 12, bottom: 0 }}>
              <defs>
                <linearGradient id="generatedPostsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={TREND_COLORS.generatedPosts} stopOpacity={0.22} />
                  <stop offset="95%" stopColor={TREND_COLORS.generatedPosts} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="day"
                tickLine={false}
                axisLine={false}
                tickFormatter={formatTrendDay}
              />
              <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={32} />
              <Tooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(label) => formatTrendDay(String(label))}
                    valueFormatter={(value) => formatProductStatsInteger(Number(value ?? 0))}
                  />
                }
              />
              <Legend verticalAlign="top" height={28} iconType="circle" />
              <Area
                type="monotone"
                dataKey="generatedPosts"
                name="Generated posts"
                stroke={TREND_COLORS.generatedPosts}
                fill="url(#generatedPostsFill)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="completedOnboardings"
                name="Onboardings"
                stroke={TREND_COLORS.completedOnboardings}
                fill="transparent"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="newBrands"
                name="New brands"
                stroke={TREND_COLORS.newBrands}
                fill="transparent"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="failedWorkerRuns"
                name="Failed runs"
                stroke={TREND_COLORS.failedWorkerRuns}
                fill="transparent"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartFrame>
      </CardContent>
    </Card>
  );
}

function DurationChart({ overview }: { overview: ProductStatsOverview }) {
  const rows = overview.durationByJobType.slice(0, 8).map((item) => ({
    jobType: item.jobType,
    label: formatJobTypeLabel(item.jobType),
    medianMinutes: item.medianMs === null ? 0 : item.medianMs / 60_000,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Slowest Job Medians</CardTitle>
        <p className="text-sm text-muted-foreground">
          Median completed worker duration by job type.
        </p>
      </CardHeader>
      <CardContent>
        <ChartFrame className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
            >
              <CartesianGrid horizontal={false} />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${Math.round(Number(value))}m`}
              />
              <YAxis
                type="category"
                dataKey="label"
                tickLine={false}
                axisLine={false}
                width={150}
              />
              <Tooltip
                content={
                  <ChartTooltipContent
                    valueFormatter={(value) => `${Number(value ?? 0).toFixed(1)}m`}
                  />
                }
              />
              <Bar
                dataKey="medianMinutes"
                name="Median"
                fill={DURATION_COLOR}
                radius={[0, 6, 6, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartFrame>
      </CardContent>
    </Card>
  );
}

function StatusBreakdownCard({ title, counts }: { title: string; counts: StatusCount[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {counts.length > 0 ? (
          counts.map((item) => (
            <Badge key={item.status} variant="outline" className={getStatusToneClass(item.status)}>
              {formatProductStatsStatusLabel(item.status)}: {item.count}
            </Badge>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">No status rows to display.</span>
        )}
      </CardContent>
    </Card>
  );
}

export function ProductStatsStatusGrid({ overview }: { overview: ProductStatsOverview }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <StatusBreakdownCard title="Brands" counts={overview.statusCounts.brands} />
      <StatusBreakdownCard
        title="Generation Tasks"
        counts={overview.statusCounts.generationTasks}
      />
      <StatusBreakdownCard title="Publications" counts={overview.statusCounts.publications} />
      <StatusBreakdownCard
        title="Social Connections"
        counts={overview.statusCounts.socialConnections}
      />
    </div>
  );
}

export function ProductStatsBrandErrorTable({ rows }: { rows: BrandErrorRate[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Brands Needing Attention</CardTitle>
        <p className="text-sm text-muted-foreground">
          Ranked by worker run error rate for the selected window.
        </p>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead>Error Rate</TableHead>
                <TableHead>Errors</TableHead>
                <TableHead>Runs</TableHead>
                <TableHead>Last Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.brandId}>
                    <TableCell>
                      <Link to={`/brands/${row.brandId}`} className="font-medium hover:underline">
                        {row.sourceDomain}
                      </Link>
                      <CodeText className="mt-1 block text-[11px]">{row.brandId}</CodeText>
                    </TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">
                      {formatProductStatsRate(row.errorRate)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.errorRuns} total / {row.failedRuns} failed / {row.retryRuns} retry
                    </TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">
                      {row.totalRuns}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(row.lastErrorAt)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableEmptyRow colSpan={5}>
                    No brand-level worker errors in this window.
                  </TableEmptyRow>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}

function ProductStatsCostTable({ overview }: { overview: ProductStatsOverview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Top LLM Spend</CardTitle>
        <p className="text-sm text-muted-foreground">Highest cost brands for this window.</p>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Events</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overview.topCostBrands.length > 0 ? (
                overview.topCostBrands.map((row) => (
                  <TableRow key={row.brandId}>
                    <TableCell>
                      <Link to={`/brands/${row.brandId}`} className="font-medium hover:underline">
                        {row.sourceDomain}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">
                      {formatMoneyUsd(row.totalCostUsd, { precision: 2 })}
                    </TableCell>
                    <TableCell className="font-mono text-sm tabular-nums">
                      {row.eventCount}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableEmptyRow colSpan={3}>No LLM usage events in this window.</TableEmptyRow>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}

function ProductStatsSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-9 w-72" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-32" />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.8fr)]">
        <Skeleton className="h-96" />
        <Skeleton className="h-96" />
      </div>
    </div>
  );
}

export function ProductStatsPage() {
  const [range, setRange] = useState<ProductStatsRange>("30d");
  const queryInput = useMemo(() => buildProductStatsOverviewInput(range), [range]);
  const { data, isPending, error } = trpc.admin.stats.overview.useQuery(queryInput);

  if (isPending) {
    return <ProductStatsSkeleton />;
  }

  if (error || !data) {
    return (
      <QueryErrorState
        title="Failed to load product stats"
        message={error?.message ?? "No stats were returned."}
        isForbidden={error?.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Product Stats"
        description="A compact readout of onboarding, generation, reliability, cost, and publishing health."
        meta={`${data.window.days} day window`}
        actions={<SegmentedControl options={RANGE_OPTIONS} value={range} onChange={setRange} />}
      />

      <div className="rounded-xl border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        Onboarding time is measured from {data.definitions.onboardingTime}. Window:{" "}
        {formatDateTime(data.window.from)} to {formatDateTime(data.window.to)}.
      </div>

      <ProductStatsKpiGrid overview={data} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.8fr)]">
        <TrendChart overview={data} />
        <DurationChart overview={data} />
      </div>

      <ProductStatsStatusGrid overview={data} />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <ProductStatsBrandErrorTable rows={data.brandErrorRates} />
        <ProductStatsCostTable overview={data} />
      </div>
    </div>
  );
}
